import { scf } from 'tencentcloud-sdk-nodejs-scf'
import { type BasicOptions } from '../index'
import { loopUntilCondition } from '../utils/promise'

export interface FunctionTarget extends BasicOptions {
  functionName: string
}

export interface FunctionContext {
  client: InstanceType<typeof scf.v20180416.Client>
  functionName: string
  namespace: string
}

const FAILED_FUNCTION_STATUSES = new Set([
  'CreateFailed',
  'UpdateFailed',
  'PublishFailed',
  'DeleteFailed'
])

const FAILED_LAYER_STATUSES = new Set(['PublishFailed', 'DeleteFailed'])
const SCF_REQUEST_TIMEOUT_SECONDS = 120

export function createFunctionContext(options: FunctionTarget): FunctionContext {
  assertRequiredOption('functionName', options.functionName)
  assertRequiredOption('region', options.region)
  assertRequiredOption('namespace', options.namespace)
  assertRequiredOption('secretId', options.secretId)
  assertRequiredOption('secretKey', options.secretKey)

  const ScfClient = scf.v20180416.Client
  const client = new ScfClient({
    credential: {
      secretId: options.secretId,
      secretKey: options.secretKey
    },
    region: options.region,
    profile: {
      httpProfile: {
        reqTimeout: SCF_REQUEST_TIMEOUT_SECONDS
      }
    }
  })

  return {
    client,
    functionName: options.functionName,
    namespace: options.namespace
  }
}

export async function waitForFunctionActive(
  context: FunctionContext,
  timeout: number = 5 * 60 * 1000
) {
  await loopUntilCondition(async () => {
    const response = await context.client.GetFunction({
      FunctionName: context.functionName,
      Namespace: context.namespace,
      Qualifier: '$LATEST',
      ShowCode: 'FALSE'
    })
    const status = response.Status

    console.log('Function status:', status)
    if (status && FAILED_FUNCTION_STATUSES.has(status)) {
      throw new Error(`Function entered terminal failure state: ${status}`)
    }

    return status === 'Active'
  }, 1000, timeout)
}

export async function waitForLayerActive(
  client: FunctionContext['client'],
  layerName: string,
  layerVersion: number,
  timeout: number = 10 * 60 * 1000
) {
  await loopUntilCondition(async () => {
    const response = await client.GetLayerVersion({
      LayerName: layerName,
      LayerVersion: layerVersion
    })
    const status = response.Status

    console.log('Layer status:', status)
    if (status && FAILED_LAYER_STATUSES.has(status)) {
      throw new Error(`Layer entered terminal failure state: ${status}`)
    }

    return status === 'Active'
  }, 1000, timeout)
}

function assertRequiredOption(name: string, value: string) {
  if (!value?.trim()) {
    throw new Error(`Missing required option: ${name}`)
  }
}